import { GearSixIcon, SignOutIcon } from '@phosphor-icons/react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UserAvatar } from './UserAvatar';
import { Organization } from '@/types/user';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { AppRoutes } from '@/constants/routes';

interface UserMenuDropdownProps {
    name: string;
    email?: string;
    organization?: Organization;
    isOrgOwner?: boolean;
    canViewSettings?: boolean;
    onLogout: () => void;
    side?: 'top' | 'bottom';
    showName?: boolean;
}

export function UserMenuDropdown({
    name,
    email,
    organization,
    isOrgOwner = false,
    canViewSettings = false,
    onLogout,
    side = 'bottom',
    showName = false,
}: UserMenuDropdownProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);

    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 outline-none">
                    <UserAvatar name={name} />
                    {showName && (
                        <span className="text-sm text-white-100">{name}</span>
                    )}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                side={side}
                align={side === 'top' ? 'end' : 'start'}
                sideOffset={8}
                className="min-w-[200px] z-[80]"
            >
                <div className="px-3 py-2">
                    <p className="text-sm font-medium text-white-100">{name}</p>
                    {email && (
                        <p className="text-xs text-grey-300 mt-0.5">{email}</p>
                    )}
                    {organization && (
                        <p className="text-xs text-grey-300 mt-0.5">
                            {organization.name}
                        </p>
                    )}
                </div>
                <DropdownMenuSeparator className="mx-1 bg-grey-600" />
                <DropdownMenuGroup>
                    {(isOrgOwner || canViewSettings) && (
                        <DropdownMenuItemBase asChild>
                            <Link to={AppRoutes.SETTINGS}>
                                <GearSixIcon className="size-4" weight="bold" />
                                <span>{t('navigation.settings')}</span>
                            </Link>
                        </DropdownMenuItemBase>
                    )}
                    <DropdownMenuItemBase onSelect={onLogout}>
                        <SignOutIcon className="size-4" weight="bold" />
                        <span>{t('navigation.logout')}</span>
                    </DropdownMenuItemBase>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
